import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import {
  nuloSeNaoEncontrado,
  type PaginaComPermissoes,
} from '@/modulos/operacao/cadastros/leitura';
import { parametrosDaListagem } from '@/modulos/operacao/comum/contrato';
import { naoInsistirEmRecusa } from '@/modulos/operacao/comum/dados';
import { chamarApi, type ErroApi } from '@/nucleo/auth/cliente-api';
import { exportarCsv } from '@/nucleo/exportarCsv';

import type { FiltroDeMonitores, Monitor, MonitorDetalhe } from './contrato';

/**
 * A camada de dados dos monitores: lista, detalhe e exportação, lidos da API
 * (`apps/api/src/modulos/monitores`). Só leitura — cadastro e mudança são da
 * J-05. O CPF já chega mascarado.
 */

export const CAMINHO_DA_API = '/bff/v1/monitores';

export const CHAVES_DE_MONITORES = {
  lista: (filtro: FiltroDeMonitores) => ['monitores', 'lista', filtro] as const,
  exportacao: (filtro: Omit<FiltroDeMonitores, 'pagina' | 'tamanho'>) =>
    ['monitores', 'exportacao', filtro] as const,
  detalhe: (slug: string) => ['monitores', 'detalhe', slug] as const,
};

/** Teto da exportacao: o mesmo da API (`TETO_DA_EXPORTACAO`). */
export const TETO_DA_EXPORTACAO = 1000;

export function useMonitores(
  filtro: FiltroDeMonitores,
): UseQueryResult<PaginaComPermissoes<Monitor>, ErroApi> {
  return useQuery<PaginaComPermissoes<Monitor>, ErroApi>({
    queryKey: CHAVES_DE_MONITORES.lista(filtro),
    queryFn: ({ signal }) =>
      chamarApi<PaginaComPermissoes<Monitor>>(
        `${CAMINHO_DA_API}${parametrosDaListagem({ ...filtro })}`,
        { sinal: signal },
      ),
    placeholderData: (anterior) => anterior,
    retry: naoInsistirEmRecusa,
  });
}

/** O monitor de `/operacao/monitores/:slug`; `null` quando nao existe ou o recorte nao mostra. */
export function useMonitor(
  slug: string | undefined,
): UseQueryResult<MonitorDetalhe | null, ErroApi> {
  return useQuery<MonitorDetalhe | null, ErroApi>({
    queryKey: CHAVES_DE_MONITORES.detalhe(slug ?? ''),
    enabled: !!slug,
    queryFn: ({ signal }) =>
      chamarApi<MonitorDetalhe>(`${CAMINHO_DA_API}/${encodeURIComponent(slug ?? '')}`, {
        sinal: signal,
      }).catch(nuloSeNaoEncontrado),
    retry: naoInsistirEmRecusa,
  });
}

/**
 * Leitura sincrona por slug, que a tela de edicao (`CadastroEdicaoPage`) ainda
 * usa. Nao ha mais copia local: o detalhe vem da API (`useMonitor`), e a
 * edicao de cadastro fica fora desta rodada (so a J-05 muda monitor).
 */
export function obterMonitorPorSlug(_slug: string | undefined): Monitor | undefined {
  return undefined;
}

/** Baixa a listagem filtrada como CSV, pela rota de exportacao, que registra o acesso. */
export function useBaixarMonitoresCsv(): (filtro: FiltroDeMonitores) => Promise<number> {
  const consultas = useQueryClient();

  return async ({ pagina: _pagina, tamanho: _tamanho, ...recorte }: FiltroDeMonitores) => {
    const resposta = await consultas.fetchQuery<PaginaComPermissoes<Monitor>>({
      queryKey: CHAVES_DE_MONITORES.exportacao(recorte),
      queryFn: () =>
        chamarApi<PaginaComPermissoes<Monitor>>(
          `${CAMINHO_DA_API}/exportacao${parametrosDaListagem({ ...recorte })}`,
        ),
      staleTime: 0,
    });

    exportarCsv(
      'monitores.csv',
      ['Nome', 'CPF', 'Vínculo', 'Cadastro', 'Situação'],
      resposta.itens.map((monitor) => [
        monitor.nome,
        monitor.cpf ?? '',
        monitor.tipo_vinculo_empregatorio ?? '',
        monitor.estado_cadastro ?? '',
        monitor.status,
      ]),
    );

    return resposta.itens.length;
  };
}
